<template>
  <div class="bg-white p-[24px] -mt-[8px] rounded-[8px] box-shadow-[0_4px_8px_0_rgba(0,0,0,0.1)] min-h-[calc(100vh-100px)]">
    <div class="flex items-center gap-[16px] mb-[24px]">
      <b-button
        variant="primary"
        size="sm"
        class="!p-[8px]"
        @click="$router.go(-1)"
      >
        <span class="k-arrow-left-2 text-[16px] font-bold" />
      </b-button>
      <h2 class="text-[20px] font-semibold text-[#333333] mb-0">
        Marketplace
      </h2>
    </div>
    <div class="flex items-center gap-[16px] mb-[8px] pb-[8px] overflow-x-auto whitespace-nowrap">
      <router-link
        v-for="status in statusList"
        :key="status.value"
        :to="changeStatus(status)"
        :class="{
          'bg-[#F95031] text-white hover:text-white border-[#F95031]': isActiveTab(status),
          'bg-white text-[#333333] border-[#E2E2E2]': !isActiveTab(status),
        }"
        class="transition-all duration-300 border font-normal text-[#333333] text-[14px] rounded-[12px] px-[16px] py-[8px] hover:shadow-[0_4px_8px_0_rgba(0,0,0,0.1)] whitespace-nowrap"
      >
        {{ status.label }}
      </router-link>
    </div>

    <div class="flex items-center justify-between flex-wrap gap-[16px] mb-[8px] pb-[8px]">
      <div class="flex items-center gap-[12px] justify-start">
        <div class="relative inline-block per-page-select-wrapper">
          <b-form-select
            v-model="paginationLimit"
            :options="[10, 20, 50, 100]"
            label="Per Page"
            class="!w-[70px] !h-full !rounded-[8px] !border-[#E2E2E2] !py-[10px] !px-[12px]"
          />
          <span class="k-arrow-down-1 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-base" />
        </div>
        <dropdown-nested
          :options="dropdownFilterOptions"
          :selected-options="filterFromQuery"
          @apply="onFilterApply"
        />
      </div>

      <div class="flex items-center gap-[12px] justify-end">
        <date-picker
          v-model="dateRange"
          class="w-full sm:w-[300px]"
          input-container-class="flex-row-reverse"
          :ranges="datePickerRangeGeneral"
          :date-format="createDatePickerRestrictor()"
        />
        <b-button
          variant="primary"
          size="sm"
          class="!p-[8px]"
          :disabled="selectedSize <= 0 || isPrintingOrder"
          @click="requestPrintOrder"
        >
          <b-spinner
            v-if="isPrintingOrder"
            small
            variant="white"
          />
          <span
            v-else
            class="k-import-2 text-[16px] font-bold"
          />
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          class="!p-[8px]"
          :disabled="isRefreshing"
          @click="handleRefresh"
        >
          <div :class="{ 'reverse-spin': isRefreshing }">
            <span
              class="k-refresh text-[16px] font-bold"
            />
          </div>
        </b-button>
      </div>
    </div>

    <collapse-header
      ref="collapseHeader"
      @on-checked-change="handleCheckedAllChange"
    />

    <div
      v-if="!isLoadingOrderList && !isErrorOrderList && orderList.length === 0"
      class="flex items-center justify-center h-[100px]"
    >
      <p class="text-[#333333] mb-0">
        Tidak ada data order yang ditemukan
      </p>
    </div>

    <div
      v-if="isLoadingOrderList && orderList.length === 0"
      class="flex items-center justify-center h-[100px] gap-[8px]"
    >
      <b-spinner
        small
        variant="primary"
      />
      <p class="text-[#333333] mb-0">
        Memuat data order...
      </p>
    </div>

    <div
      v-if="isErrorOrderList && !isLoadingOrderList"
      class="flex flex-col gap-[8px] items-center justify-center h-[100px]"
    >
      <div class="flex items-center gap-[8px]">
        <span class="k-danger text-[#E31A1A] border-[1px] border-[#E31A1A] bg-[#FFEDED] rounded-[4px] size-[24px] flex items-center justify-center rounded-full text-[14px] m-0" />
        <p class="text-[#333333] mb-0">
          Gagal mengambil data order, silahkan coba lagi
        </p>
      </div>
      <b-button
        variant="primary"
        size="sm"
        @click="refreshData"
      >
        Coba Lagi
      </b-button>
    </div>

    <collapse-card
      v-for="(order, index) in orderList"
      :key="index"
      :data="order"
      :default-visible="index <= 10"
      :packing-materials-fetcher="packingMaterialsFetcher"
      :edited-order-data="editedOrderData"
      :checked-value="orderSelected.includes(index)"
      @on-checked-change="(data) => handleCheckedChange(data, index)"
      @on-save-packing="handleSavePacking"
    />

    <load-more-data
      :fetcher="orderListFetcher"
      :show-all-data-loaded="false"
    />

    <div
      v-if="isShowFulfillmentResume"
      class="flex flex-col gap-[24px] items-end justify-end mt-[24px] mr-[40px]"
    >
      <fulfillment-resume
        :status="currentStatus"
        :total-fulfillment-fee="totalFulfillmentFee"
        :total-order="selectedSize"
        :upgrade-fulfillment="newFulfillmentFee"
      />
    </div>
    <order-action
      v-show="isShowFulfillmentResume"
      ref="orderAction"
      :is-submitting-order="isSubmittingOrder"
      :selected-size="selectedSize"
      :status="currentStatus"
      @click="submitOrder"
    />

    <modal-loading-download ref="modalLoadingDownload" />
    <modal-un-printed-order ref="modalUnPrintedOrder" />
  </div>
</template>

<script>
import moment from 'moment'
import { toastError, toastSuccess } from '@/libs/toastification'
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import { firstDateOfMonth, generateLastDays, today } from '@/store/helpers'
import { createDatePickerRestrictor, YEAR_MONTH_DAY } from '@/libs/filterDate'
import useBatchMutation from '@/@core/libs/fetch/useBatchMutation'
import { invalidateQuery } from '@/@core/libs/fetch/useCacheableFetcher'
import { statusList } from './config'
import DropdownNested from './DropdownNested.vue'
import FilterMarketplace from './FilterMarketplace.vue'
import FilterSeller from './FilterSeller.vue'
import DatePicker from '../../storage-request/DatePicker.vue'
import { datePickerRangeGeneral } from '../../storage-request/config'
import CollapseHeader from './CollapseHeader.vue'
import CollapseCard from './CollapseCard.vue'
import FulfillmentResume from './FulfillmentResume.vue'
import OrderAction from './OrderAction.vue'
import ModalLoadingDownload from './ModalLoadingDownload.vue'
import LoadMoreData from '../non-komship/LoadMoreData.vue'
import ModalUnPrintedOrder from './ModalUnPrintedOrder.vue'

export default {
  name: 'MarketplacePage',
  components: {
    DropdownNested,
    DatePicker,
    CollapseHeader,
    CollapseCard,
    FulfillmentResume,
    OrderAction,
    ModalLoadingDownload,
    LoadMoreData,
    ModalUnPrintedOrder,
  },
  data() {
    return {
      statusList,
      paginationLimit: 10,
      selectedMarketplace: null,
      dateRange: {
        startDate: generateLastDays(today, 14),
        endDate: today,
      },
      orderSelected: [],
      isPrintingOrder: false,
      editedOrderData: {},
      isRefreshing: false,
      filterFromQuery: {
        marketplace: [],
        seller: [],
      },
      isSubmittingOrder: false,
    }
  },
  computed: {
    currentStatus() {
      return this.$route.query.status || 'submitted'
    },
    isOnDefaultStatus() {
      return this.$route.query.status === 'all' || this.$route.query.status === undefined
    },
    isShowFulfillmentResume() {
      return this.currentStatus === 'submitted'
    },
    dropdownFilterOptions() {
      return [
        { text: 'Marketplace', value: 'marketplace', component: FilterMarketplace },
        {
          text: 'Seller',
          value: 'seller',
          component: FilterSeller,
          componentProps: () => ({ sellersFetcher: this.sellersFetcher }),
        },
      ]
    },
    orderList() {
      return this.orderListFetcher?.data
    },
    isLoadingOrderList() {
      return this.orderListFetcher?.isLoading || false
    },
    isErrorOrderList() {
      return this.orderListFetcher?.error || false
    },
    selectedSize() {
      return this.orderSelected?.length || 0
    },
    totalFulfillmentFee() {
      return this.orderSelected?.reduce((acc, index) => acc + this.orderList[index]?.fulfillment_fee, 0) || 0
    },
    newFulfillmentFee() {
      return this.orderSelected?.reduce((acc, index) => {
        const newData = this.editedOrderData[this.orderList[index]?.id]
        return acc + (newData?.grand_fulfillment_cost || this.orderList[index]?.fulfillment_fee)
      }, 0) || 0
    },
    fulfillmentFeeDifference() {
      return this.totalFulfillmentFee - this.newFulfillmentFee
    },
    filterParams() {
      return {
        shop_id: this.filterFromQuery.seller?.join(','),
        marketplace: this.filterFromQuery.marketplace?.join(','),
      }
    },
  },
  watch: {
    paginationLimit(value) {
      this.refreshData()
      const nextLimit = String(value)
      if (this.$route.query.limit === nextLimit) return
      this.$router.push({ query: { ...this.$route.query, limit: nextLimit } })
    },
    dateRange(value) {
      this.refreshData()
      const startDate = YEAR_MONTH_DAY(value.startDate)
      const endDate = YEAR_MONTH_DAY(value.endDate)
      if (this.$route.query.start_date === startDate && this.$route.query.end_date === endDate) return
      this.$router.push({ query: { ...this.$route.query, start_date: startDate, end_date: endDate } })
    },
    currentStatus() {
      this.refreshData()
      this.orderSelected = []
      this.$refs.collapseHeader.setChecked(false)
    },
  },
  created() {
    this.initializeSellersFetcher()
    this.datePickerRangeGeneral = datePickerRangeGeneral(today)
    this.initializePackingMaterialsFetcher()
    this.initializeOrderListFetcher()
    this.initializeBatchMutation()

    const startDateQuery = this.$route.query.start_date
    const endDateQuery = this.$route.query.end_date
    if (startDateQuery || endDateQuery) {
      this.dateRange = {
        startDate: startDateQuery && moment(startDateQuery).isValid() ? YEAR_MONTH_DAY(startDateQuery) : firstDateOfMonth,
        endDate: endDateQuery && moment(endDateQuery).isValid() ? YEAR_MONTH_DAY(endDateQuery) : today,
      }
    }

    const { query } = this.$route
    const limitNum = Number(query.limit)
    if (_.includes([10, 20, 50, 100], limitNum)) this.paginationLimit = limitNum

    _.assign(this.filterFromQuery, this.queryToFilter(query))
  },
  mounted() {
    this.refreshData()
  },
  methods: {
    createDatePickerRestrictor,
    onFilterApply(payload) {
      if (!payload?.label) return

      this.$set(this.filterFromQuery, payload.label, payload.checkedOptions)
      if (payload.label === 'seller') {
        this.$router.push({ query: { ...this.$route.query, seller: this.filterParams.shop_id } })
      }
      if (payload.label === 'marketplace') {
        this.$router.push({ query: { ...this.$route.query, marketplace: this.filterParams.marketplace } })
      }

      this.refreshData()
    },
    isActiveTab(status) {
      return this.currentStatus === status.value
    },
    changeStatus(status) {
      const query = { ...this.$route.query, status: status.value }
      const queryString = new URLSearchParams(query).toString()
      return `/outbound/marketplace?${queryString}`
    },
    /**
     * Initialize the batch mutation used for constrainting the number of requests to the server when submitting orders
     */
    initializeBatchMutation() {
      let thereIsError = false
      this.batchMutation = useBatchMutation({
        batchSize: 10,
        stopOnError: false,
        shouldStop: false,
        onBatchComplete: values => {
          thereIsError = values.some(value => value.status === 'rejected')
        },
        onComplete: () => {
          this.isSubmittingOrder = false
          this.refreshData()
          if (thereIsError) {
            toastError('Terdapat order yang gagal di proses, silahkan coba lagi')

            return
          }

          this.orderSelected = []
          this.$refs.collapseHeader.setChecked(false)

          toastSuccess('Order berhasil di proses')
        },
      })
    },
    /**
     * Initialize the packing materials fetcher used for fetching the packing materials from the server
     */
    initializePackingMaterialsFetcher() {
      const fnFetch = ({
        offset, limit, search, signal,
      }) => this.$http_new.get('/komship/api/v1/packing-materials', {
        params: {
          offset,
          limit,
          search,
        },
        signal,
      }).then(res => {
        const { data } = res.data
        return {
          data,
        }
      }).catch(() => {
        toastError('Gagal mengambil data bahan packing, silahkan coba lagi')
        return {
          data: [],
        }
      })

      this.packingMaterialsFetcher = createPaginatedFetcher(fnFetch, 0, 50)
    },
    /**
     * Request to print the order labels and process the response
     */
    async requestPrintOrder() {
      this.isPrintingOrder = true
      this.$refs.modalLoadingDownload.showModal()
      try {
        const { data } = await this.$http_new.post('/label/api/v1/marketplace-labels/orders', {
          order_ids: this.orderSelected?.map(index => this.orderList[index]?.id),
        }, {
          responseType: 'blob',
        })

        // if the response is a JSON, then we can show the error message
        if (data && data.type && data.type.includes('application/json')) {
          toastError('Gagal mencetak order, silahkan coba lagi')
          return
        }

        // if the response is a blob, then we can download the file
        if (data instanceof Blob) {
          const fileName = `marketplace-order-labels-${moment().format('DD-MM-YYYY_HH-mm')}.pdf`
          const url = window.URL.createObjectURL(data)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(link)
          toastSuccess('Order berhasil dicetak ke perangkat Anda.', 'Cetak Order Berhasil')
        } else {
          toastError('Gagal mencetak order, silahkan coba lagi')
        }
        this.refreshData()
      } catch (error) {
        toastError('Gagal mencetak order, silahkan coba lagi')
      } finally {
        this.isPrintingOrder = false
        this.$refs.modalLoadingDownload.closeModal()
      }
    },
    mutateOrder({ orderId, payload }) {
      return this.$http_new.post(`/komship/api/v1/marketplace-orders/${orderId}/mitras/confirm`, payload)
    },
    mutateShipOrder({ orderId }) {
      return this.$http_new.post(`/komship/api/v1/marketplace-orders/${orderId}/mitras/ship`)
    },
    /**
     * Handle submission of the order to be shipped - been in confirmed status.
     * If there is unprinted order, it will be blocked to ship and show modal to user.
     * Otherwise, it will proceed to ship the order.
     */
    submitShipOrder() {
      const hasUnprintedOrder = this.orderSelected.reduce((acc, index) => {
        if (!this.orderList[index]?.is_printed) {
          const { marketplace_order_id, marketplace } = this.orderList[index]
          acc.push({
            marketplace_order_id,
            marketplace,
          })
        }
        return acc
      }, [])

      if (hasUnprintedOrder.length > 0) {
        this.isSubmittingOrder = false
        this.$refs.modalUnPrintedOrder.show({
          data: hasUnprintedOrder,
        })
        return
      }

      const payload = this.orderSelected.map(index => () => this.mutateShipOrder({ orderId: this.orderList[index]?.id }))

      this.batchMutation.run(payload)
    },
    /**
     * Handle submission of the order to be packed - been in submitted status
     */
    submitPackingOrder() {
      const payloads = this.orderSelected.map(index => {
        const data = this.orderList[index]
        const editedData = this.editedOrderData[data?.id]

        const payload = {
          fulfillment_fee: editedData?.grand_fulfillment_cost || data?.fulfillment_fee,
          packing_cost: editedData?.packing_cost || data?.packing_cost,
          is_upgrade_packing: editedData?.packings?.length > 0,
          packings: editedData?.packings || data?.packing_materials,
        }

        return () => this.mutateOrder({ orderId: data?.id, payload })
      })

      this.batchMutation.run(payloads)
    },
    /**
     * Handle submission of the order to the server based on user action
     */
    submitOrder() {
      this.isSubmittingOrder = true

      if (this.currentStatus === 'confirmed') {
        this.submitShipOrder()

        return
      }

      if (this.currentStatus === 'submitted') {
        this.submitPackingOrder()

        return
      }

      this.isSubmittingOrder = false
    },
    /**
     * Handle the checked change event and check if all the orders are selected
     * @param data {Object} - The data object containing the state and index
     * @param index {Number} - The index of the order
     * @returns {void}
     */
    handleCheckedChange(data, index) {
      const next = new Set(this.orderSelected)
      if (data.state) {
        next.add(index)
      } else {
        next.delete(index)
      }
      this.orderSelected = Array.from(next)
      this.$refs.collapseHeader.setChecked(this.orderSelected.length >= this.orderList.length)
    },
    handleCheckedAllChange(state) {
      this.orderSelected = !state ? [] : this.orderList.map((_, index) => index)
    },
    async refreshData() {
      this.isRefreshing = true
      this.orderListFetcher.cancel()
      await this.orderListFetcher.fetchData({
        reset: true,
        options: {
          limit: this.paginationLimit,
          search: this.search,
          start_date: this.dateRange.startDate,
          end_date: this.dateRange.endDate,
          status: this.currentStatus,
          ...this.filterParams,
        },
      })
      this.isRefreshing = false
    },
    /**
     * Initialize the order list fetcher used for fetching the order list from the server
     * @returns {void}
     */
    initializeOrderListFetcher() {
      const fnFetch = ({
        offset, limit, shop_id, marketplace, awb, status, start_date, end_date, cancelToken,
      }) => this.$http_new.get('/komship/api/v1/marketplace-orders/mitras/summaries', {
        params: {
          offset,
          limit,
          shop_id,
          marketplace,
          awb,
          status,
          start_date: YEAR_MONTH_DAY(start_date),
          end_date: YEAR_MONTH_DAY(end_date),
        },
        cancelToken,
      }).then(res => {
        const { data } = res.data
        return {
          data,
        }
      }).catch(() => {
        throw new Error('Gagal mengambil data order, silahkan coba lagi')
      })

      this.orderListFetcher = createPaginatedFetcher(fnFetch, 0, this.paginationLimit)
    },
    handleSavePacking(message) {
      this.$set(this.editedOrderData, message.position, message.data)
    },
    calculateFulfillmentFee(order) {
      const editedData = this.editedOrderData[order?.id]
      const originalFulfillmentFee = order?.fulfillment_fee

      return editedData?.grand_fulfillment_cost || originalFulfillmentFee
    },
    queryArray(val) {
      if (!_.isString(val)) return []
      return _.compact(_.map(_.split(val, ','), _.trim))
    },
    /**
     * Convert the query parameters to the filter parameters
     * @param q {Object} - The query parameters
     * @returns {Object} - The filter parameters
     */
    queryToFilter(q) {
      return _.mapValues(
        { marketplace: _.get(q, 'marketplace'), seller: _.get(q, 'seller') },
        this.queryArray,
      )
    },
    /**
     * Initialize the sellers fetcher used for fetching the sellers from the server
     * @returns {void}
     */
    initializeSellersFetcher() {
      const fnFetch = ({
        offset, limit, cancelToken,
      }) => this.$http_new.get('/komship/api/v1/marketplace-shops/warehouses', {
        params: {
          offset,
          limit,
        },
        cancelToken,
      }).then(res => {
        const { data } = res.data
        return {
          data,
        }
      }).catch(() => {
        throw new Error('Gagal mengambil data seller, silahkan coba lagi')
      })

      this.sellersFetcher = createPaginatedFetcher(fnFetch, 0, 50)
    },
    handleRefresh() {
      invalidateQuery(['/komship/api/v1/marketplace-orders/'])
      this.refreshData()
    },
  },
}
</script>

<style scoped>
.per-page-select-wrapper ::v-deep .custom-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none;
  padding-right: 1.5rem;
}

@keyframes reverse-spin {
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0deg);
  }
}

.reverse-spin {
  animation: reverse-spin 1s linear infinite;
}
</style>
